/* eslint-disable vue/max-len */
import AuctionClient from '@/api/AuctionClient';
import AuctionProductsVdpClient from '@/api/AuctionProductsVdpClient';
import TransportationGatewayClient from '@/api/TransportGatewayClient';
import { makeOfferStates } from '@/constants/auctionStates';
import featureFlagSlugs from '@/constants/featureFlagSlugs';
import chunk from '@/utilities/chunk';
import {
  setOneTimeMaxBiddingEnabledForSeller,
  setMinProxyValidationEnabledForSeller,
  isSellerInfoCached,
} from '@/utilities/marketplace/sellerInfoCache';

/**
 * Utility functions for handling additional auction data fetching
 * Used by marketplace-auctions Vuex store to process supplementary data (VDP/SRP)
 *
 * Each helper is a [filterFn, apiCallFn] pair. Both functions receive
 * a storeContext object { dispatch, state } passed from the Vuex action.
 * Filter callbacks typically use `dispatch` (with `{ root: true }`) to evaluate
 * feature flags; API callbacks use `state.authorization.user` to construct clients.
 */

class AuctionAdditionalDataHelpers {
  /**
   * @typedef {(auctions: Array<import('../../models/Auction').default>, storeContext: {dispatch: Function, state: Object}) => Promise<Array<import('../../models/Auction').default>>} Filter
   * @typedef {(auctions: Array<import('../../models/Auction').default>, storeContext: {dispatch: Function, state: Object}) => Promise<Array<{id: number} & import('../../models/Auction').default>>} ApiCall
   *
   * @type {Array<[Filter, ApiCall]>}
   */
  static helpers = [
    AuctionAdditionalDataHelpers.highestRejectedOffers(),
    AuctionAdditionalDataHelpers.sellerInfoDisplay(),
    AuctionAdditionalDataHelpers.transportPromos(),
  ];

  /**
   * @return {[Filter, ApiCall]}
   */
  static highestRejectedOffers() {
    return [
      (auctions) => auctions.filter((auction) => {
        if (auction.type === 'saved') {
          return true;
        }
        const isOfferState = makeOfferStates.includes(auction.status);
        return auction.canMakeOffer || (isOfferState && auction.isMemberOfSellingDealership);
      }),
      async (auctions, { state, dispatch }) => {
        const auctionClient = new AuctionClient(state.authorization.user);

        const isSavedAuction = auctions.length > 0 && auctions[0].type === 'saved';
        const ids = isSavedAuction
          ? auctions.map((a) => a.savedAuctionId || a.id)
          : auctions.map((a) => a.id);

        const endedAuctionPurchaseEnabled = await dispatch(
          'getFlag',
          featureFlagSlugs.offersAndNegotiationsEndedAuctionPurchase,
          { root: true },
        );

        if (endedAuctionPurchaseEnabled) {
          const response = await auctionClient.getAttributes(
            ['highest_rejected_offer_amount', 'ended_auction_purchase_amount'],
            ids,
            isSavedAuction,
          );

          return response.data.data.results
            .map((item) => {
              const result = { id: isSavedAuction ? item.saved_auction_id : item.auction_id };
              const highestRejectedOfferAmount = item.attributes?.highest_rejected_offer_amount;
              const endedAuctionPurchaseAmount = item.attributes?.ended_auction_purchase_amount;

              if (highestRejectedOfferAmount?.amount) {
                result.highestRejectedOffer = {
                  amount: highestRejectedOfferAmount.amount,
                  rejectedAt: highestRejectedOfferAmount.rejected_at || highestRejectedOfferAmount.date,
                };
              }
              if (endedAuctionPurchaseAmount?.amount) {
                result.endedAuctionPurchase = {
                  amount: endedAuctionPurchaseAmount.amount,
                  expiresAt: endedAuctionPurchaseAmount.expires_at || null,
                };
              }
              return result;
            })
            .filter((item) => item.highestRejectedOffer || item.endedAuctionPurchase);
        }

        const response = await auctionClient.getHighestRejectedOffers(ids, isSavedAuction);

        return (
          response.data.data.restriction_offers
            .filter((item) => item.amount)
            .map((item) => ({
              id: isSavedAuction ? item.saved_auction_id : item.auction_id,
              highestRejectedOffer: {
                amount: item.amount,
                rejectedAt: item.rejected_at,
              },
            }))
        );
      },
    ];
  }

  /**
   * @return {[Filter, ApiCall]}
   */
  static sellerInfoDisplay() {
    return [
      async (auctions, { dispatch }) => {
        const sellerNameEnabled = await dispatch(
          'getFlag',
          featureFlagSlugs.auctionProductsSellerNameDisplay,
          { root: true },
        );

        return auctions.filter((i) => (
          (sellerNameEnabled && !i.sellerInfoDisplay) || !isSellerInfoCached(i.id)
        ));
      },
      async (auctions, { state, dispatch }) => {
        const sizeLimit = 100;
        const auctionProductsVdpClient = new AuctionProductsVdpClient(state.authorization.user);

        const ids = auctions.map((a) => a.id);
        const splitIds = chunk(ids, sizeLimit);

        const results = await Promise.all(
          splitIds.map((idsArray) => auctionProductsVdpClient.getSellerInfo(idsArray)),
        );

        const items = results.flatMap((response) => response.data);

        items.forEach((item) => {
          setOneTimeMaxBiddingEnabledForSeller(
            item.auctionId,
            item.dealerInfo?.isOneTimeMaxBiddingFeatureEnabledForSeller ?? false,
          );
          setMinProxyValidationEnabledForSeller(
            item.auctionId,
            item.dealerInfo?.isMinProxyValidationFeatureEnabledForSeller ?? false,
          );
        });

        const sellerNameEnabled = await dispatch(
          'getFlag',
          featureFlagSlugs.auctionProductsSellerNameDisplay,
          { root: true },
        );
        if (!sellerNameEnabled) return [];

        return items
          .filter((item) => item.dealerInfo)
          .map((item) => ({
            id: item.auctionId,
            sellerInfoDisplay: {
              dealerId: item.dealerInfo.dealerId ?? null,
              dealerName: item.dealerInfo.dealerName ?? '',
              dealerType: item.dealerInfo.dealerType ?? '',
              dealerGroup: item.dealerInfo.dealerGroup ?? '',
              isYellowLightFeatureEnabledForSeller:
                item.dealerInfo.isYellowLightFeatureEnabledForSeller ?? false,
              isVdpYellowLightFeatureEnabledForSeller:
                item.dealerInfo.isVdpYellowLightFeatureEnabledForSeller ?? false,
            },
          }));
      },
    ];
  }

  /**
   * @return {[Filter, ApiCall]}
   */
  static transportPromos() {
    return [
      async (auctions, { dispatch }) => {
      // ff grab and check for eligibility for enable_transport_promo_on_srp
        const featureEnabled = await dispatch(
          'getFlag',
          featureFlagSlugs.enableTransportPromoOnSRP,
          { root: true },
        );
        if (!featureEnabled) return [];
        // filter to uncached only
        return auctions.filter((a) => a.transportPromo === undefined);
      },
      async (auctions, { state }) => {
        const sizeLimit = 20;

        // fetch
        const client = new TransportationGatewayClient(state.authorization.user);

        const ids = auctions.map((a) => a.id);

        const splitIds = chunk(ids, sizeLimit);

        if (splitIds.length > 0) {
          const results = await Promise.all(
            splitIds.map(async (idsArray) => {
              const response = await client.fetchTransportPromos({ auctionIds: idsArray });
              const rows = response?.data?.data?.content ?? [];
              return rows.map((row) => ({
                id: row.auctionId,
                transportPromo: row.id === null ? null : row,
              }));
            }),
          );
          const transportPromos = results.flat();
          return transportPromos;
        }
        return null;
      },
    ];
  }
}
export default AuctionAdditionalDataHelpers;
